import { Checkbox, IconsUnion, Link, Radio } from '@globalmed/atlas-ui';
import { memo, ReactNode, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderLabel } from '../FormBuilderLabel';
import styles from './FormBuilderOption.module.css';
import { TFormBuilderOption, TFormBuilderVariant } from './types';
import { TNullable } from '@/types';

type TProps = {
  variant?: TFormBuilderVariant;
  option: TFormBuilderOption;
  onChange: (value: TFormBuilderOption) => void;
  onDownMove: (value: TFormBuilderOption) => void;
  onUpMove: (value: TFormBuilderOption) => void;
  onRemove?: (value: TFormBuilderOption) => void;
};

export const FormBuilderOption =
  /**
   * Single option component for multiple/single choice fields with controls for reordering and removing
   * @param {TProps} props - Component props
   * @param {TFormBuilderVariant} [props.variant] - Variant of the option (checkbox or radio)
   * @param {TFormBuilderOption} props.option - Option data
   * @param {Function} props.onChange - Callback when option value changes
   * @param {Function} props.onDownMove - Callback to move option down in the list
   * @param {Function} props.onUpMove - Callback to move option up in the list
   * @param {Function} [props.onRemove] - Optional callback to remove the option
   * @returns {JSX.Element} Form builder option component
   */
  memo(
    ({ option, variant, onChange, onDownMove, onRemove, onUpMove }: TProps) => {
      const { t } = useTranslation();
      const inputId = useId();

      const onLabelChange = useCallback(
        (value: string) => onChange({ ...option, value }),
        [onChange, option],
      );

      type TOptionAction = {
        preIcon: IconsUnion;
        'aria-label': string;
        onClick: () => void;
      };

      const allActions: Array<TNullable<TOptionAction>> = [
        {
          preIcon: 'ChevronDown',
          'aria-label': t('form-builder.move-option-down'),
          onClick: () => onDownMove(option),
        },
        {
          preIcon: 'ChevronUp',
          'aria-label': t('form-builder.move-option-up'),
          onClick: () => onUpMove(option),
        },
        onRemove
          ? {
              preIcon: 'XClose',
              'aria-label': t('form-builder.remove-option'),
              onClick: () => onRemove(option),
            }
          : null,
      ];

      const actions = allActions.filter((v): v is TOptionAction => !!v);

      const components: Record<TFormBuilderVariant, ReactNode> = {
        checkbox: <Checkbox id={inputId} disabled />,
        radio: <Radio id={inputId} disabled className={styles.radio} />,
      };

      const component = variant ? components[variant] : null;

      return (
        <div className={styles.container}>
          <div className={styles.infoBlock}>
            {component}
            <FormBuilderLabel
              label={option.value}
              htmlFor={inputId}
              onLabelChange={onLabelChange}
              placeholder={t('form-builder.type-option-text-here')}
              error={option.error}
              sx={{
                backgroundColor: 'transparent',
              }}
            />
          </div>
          <div className={styles.actions}>
            {actions.map((values) => (
              <Link {...values} key={values.preIcon.toString()} color="black" />
            ))}
          </div>
        </div>
      );
    },
  );

FormBuilderOption.displayName = 'FormBuilderOption';
